import React from 'react';
import { createTheme, PaletteColor, PaletteColorOptions } from '@mui/material';
import { themeComponents } from './components';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    headerTitle: React.CSSProperties;
    sideMenuLg: React.CSSProperties;
    sideMenuMd: React.CSSProperties;
    sideMenuSm: React.CSSProperties;
    bodyXxl: React.CSSProperties;
    bodyXl: React.CSSProperties;
    bodyLg: React.CSSProperties;
    bodyMd: React.CSSProperties;
    bodyXs: React.CSSProperties;
    label: React.CSSProperties;
    labelMd: React.CSSProperties;
    labelSm: React.CSSProperties;
    buttonSm: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    headerTitle?: React.CSSProperties;
    sideMenuLg?: React.CSSProperties;
    sideMenuMd?: React.CSSProperties;
    sideMenuSm?: React.CSSProperties;
    bodyXxl?: React.CSSProperties;
    bodyXl?: React.CSSProperties;
    bodyLg?: React.CSSProperties;
    bodyMd?: React.CSSProperties;
    bodyXs?: React.CSSProperties;
    label?: React.CSSProperties;
    labelMd?: React.CSSProperties;
    labelSm?: React.CSSProperties;
    buttonSm?: React.CSSProperties;
  }

  interface ThemeOptions {
    constants?: {
      [key: string]: unknown;
    };
  }

  interface Theme {
    constants: {
      drawerWidth: number;
      footerHeight: number;
      headerHeight: number;
    };
  }

  interface Palette {
    accent: PaletteColor;
    progress: PaletteColor;
    undecided: PaletteColor;
    withdraw: PaletteColor;
    border: PaletteColor;
    borderOpaque: PaletteColor;
  }

  interface PaletteOptions {
    accent?: PaletteColorOptions;
    progress?: PaletteColorOptions;
    undecided?: PaletteColorOptions;
    withdraw?: PaletteColorOptions;
    border?: PaletteColorOptions;
    borderOpaque?: PaletteColorOptions;
  }

  interface TypeAction {
    buttonFocus?: string;
    onCopy?: string;
  }

  interface TypeBackground {
    disabled?: string;
    done?: string;
    edited?: string;
    error?: string;
    header?: string;
    screen?: string;
    loading?: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headerTitle: true;
    sideMenuLg: true;
    sideMenuMd: true;
    sideMenuSm: true;
    bodyXxl: true;
    bodyXl: true;
    bodyLg: true;
    bodyMd: true;
    bodyXs: true;
    label: true;
    labelSm: true;
    labelMd: true;
    buttonSm: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    accent: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    accent: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    progress: true;
    undecided: true;
    withdraw: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    progress: true;
  }
}

export const theme = createTheme({
  constants: {
    drawerWidth: 240,
    footerHeight: 40,
    headerHeight: 64,
  },
  palette: {
    primary: {
      main: '#3170C1',
      light: '#F2F9FC',
      dark: '#174076',
    },
    error: {
      main: '#C83727',
      light: '#FFEDEB',
    },
    warning: {
      main: '#C83727',
      light: '#FFEDEB',
      dark: '#C83727',
    },
    info: {
      main: '#FFF2AC',
    },
    accent: {
      main: '#229984',
      dark: '#146B5C',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#469A48',
      light: '#EDFFED',
    },
    progress: {
      main: '#F59419',
      contrastText: '#FFFFFF',
    },
    undecided: {
      main: '#C7C7C7',
      contrastText: '#393939',
    },
    withdraw: {
      main: '#E155F8',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#393939',
      secondary: '#787878',
      disabled: '#C7C7C7',
    },
    divider: '#DBDBDB',
    action: {
      active: '#F2F9FC',
      hover: '#F2F9FC',
      selected: '#F2F9FC',
      disabled: '#C7C7C7',
      disabledBackground: '#F1F1F1',
      focus: '#F2F9FC',
      buttonFocus: '#F59419',
      onCopy: '#c7eeff',
    },
    background: {
      disabled: '#E3E3E3',
      done: '#EDFFED',
      edited: 'rgba(255, 231, 201, 1)',
      error: '#FFE0E0',
      header: '#3170C1',
      screen: '#F7F7F7',
      loading: 'rgba(0,0,0,0.25)',
    },
    border: {
      main: '#C7C7C7',
    },
    borderOpaque: {
      main: 'rgba(196, 196, 196, 1)',
    },
  },
  typography: {
    fontFamily: 'Meiryo',
    h1: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    h3: {
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    headerTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    sideMenuLg: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    sideMenuMd: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    sideMenuSm: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    bodyXxl: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    bodyXl: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    bodyLg: {
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    bodyMd: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    bodyXs: {
      fontSize: '0.625rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    label: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    labelMd: {
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    labelSm: {
      fontSize: '0.625rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.5,
      textTransform: 'none',
    },
    buttonSm: {
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.5,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 880,
      lg: 1200,
      xl: 1536,
    },
  },
  ...themeComponents,
});
